import React, { useRef, useMemo, useCallback } from 'react'
import { useConfiguratorContext } from '@augmatica/iframe-interface-react'

import lfcDimensionsVisibilityJson from '../../data/config/lfcDimensionsVisibility.json'
import { getDmPropsForChosenValues } from '../../helpers/getDmPropsForChosenValues'
import { getVisibleLfcDimensionsIds } from '../../helpers/getVisibleLfcDimensionsIds'
import { getPropsForUpdate } from '../../helpers/getPropsForUpdate'
import { isLfcItem as checkIsLfcItem } from '../../helpers/isLfcItem'
import { handleSelectedItem } from '../../store/selectedItem/selectedItemSlice'
import { setIsEditItemPanelActive } from '../../store/ui/uiSlice'
import MallisConfigurator from '../MallisConfigurator/MallisConfigurator'
import * as Styled from './styles'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { getItemTypeForDmItem } from '../../helpers/getItemTypeForDynaMakerItem'

const EditItemPanel: React.FC = () => {
  const dispatch = useAppDispatch()

  const configurator = useConfiguratorContext()

  const { content } = useAppSelector((state) => state.init)
  const { erpItems, lfcItems } = useAppSelector((state) => state.items)
  const { selectedItem } = useAppSelector((state) => state.selectedItem)
  const { isEditItemPanelActive } = useAppSelector((state) => state.ui)

  const selectedItemRef = useRef(selectedItem)
  selectedItemRef.current = selectedItem

  const isLfcItem = checkIsLfcItem(selectedItem as any) // TODO

  const editItemTitle =
    selectedItem.item === 'assembly' || !isLfcItem
      ? ''
      : content?.items?.[getItemTypeForDmItem(selectedItem.item) ?? '']?.editItem?.title

  const { lfcItem, pricingInfo, initialChosenValues, initialConfigResult } = useMemo(
    () =>
      isLfcItem
        ? {
            lfcItem: lfcItems.find(
              (lfcItem) => lfcItem.configuratorContextId === selectedItem.props?.configData.contextId
            ),
            pricingInfo: erpItems.find((erpItem) => erpItem.Sku === selectedItem.erpItemNumber)?.PricingInfo,
            initialChosenValues: selectedItem.props?.configData.chosenValues,
            initialConfigResult: selectedItem.props?.configData.configResult,
          }
        : { lfcItem: null, pricingInfo: null, initialChosenValues: null, initialConfigResult: null },
    [selectedItem.id]
  )

  const visibleLfcDimensionsIds = useMemo(
    () => (lfcItem ? getVisibleLfcDimensionsIds(lfcItem.dimensions, lfcDimensionsVisibilityJson) : []),
    [lfcItem?.dimensions]
  )

  const handleMallisChange = useCallback(
    ({
      selectedItemId: incomingItemId,
      chosenValues,
      configResult,
    }: {
      selectedItemId: string
      chosenValues: number[]
      configResult: any
    }) => {
      if (!isLfcItem) return

      const current = selectedItemRef.current

      if (incomingItemId !== current.id) {
        console.warn(
          'mok-context-spa: Detected unexpected deprecated mallis-config-update event -- payload is ignored.'
        )
        return
      }

      const _lastFetched = Date.now()

      const propsForChosenValues = getDmPropsForChosenValues(chosenValues, lfcItem?.dimensions)

      const updatedProps = {
        ...current.props,
        ...propsForChosenValues,
        configData: {
          ...current.props?.configData,
          _lastFetched,
          chosenValues,
          configResult,
        },
      }

      dispatch(
        handleSelectedItem({
          erpItemNumber: current.erpItemNumber,
          id: current.id,
          item: current.item,
          props: updatedProps,
        })
      )

      if (current.props) {
        const propsForUpdate = getPropsForUpdate(current.props as any, updatedProps as any) // TODO
        configurator.updateItem(current.item, current.id, propsForUpdate as any) // TODO
      }
    },
    [selectedItem.id]
  )

  if (selectedItem.item === 'assembly') return null // Not applicable

  // Both guards are needed: the memo above is keyed on selectedItem.id alone, so lfcItem can be stale while isLfcItem is fresh
  if (!isLfcItem) return null
  // A mallis without data from LFC or Scensum cannot be configured; DM's failed replacements are one way to end up here
  if (!lfcItem || !pricingInfo) return null

  return (
    <Styled.EditItemPanel
      isEditItemPanelActive={isEditItemPanelActive}
      visibleLfcDimensionsIds={visibleLfcDimensionsIds}
    >
      <div className="edit-item-header">
        {editItemTitle}
        <button
          className="button-done"
          onClick={() => {
            dispatch(setIsEditItemPanelActive(false))
            configurator.cameraToPrevious()
          }}
        >
          {/* TODO språkhanterad från content */}
          Klar
        </button>
      </div>
      <MallisConfigurator
        lfcItem={lfcItem}
        pricingInfo={pricingInfo}
        initialChosenValues={initialChosenValues}
        initialConfigResult={initialConfigResult}
        dynamakerItemId={selectedItem.id}
        onChange={handleMallisChange}
      />
    </Styled.EditItemPanel>
  )
}

export default EditItemPanel
